// src/services/jobService.js
import { getLocalJobs } from "./firestoreService";

const ADZUNA_APP_ID  = process.env.REACT_APP_ADZUNA_APP_ID;
const ADZUNA_APP_KEY = process.env.REACT_APP_ADZUNA_APP_KEY;
const JSEARCH_KEY    = process.env.REACT_APP_JSEARCH_API_KEY;

// ── Rate limiter & cache for JSearch ─────────────────────────
let lastJSearchCall = 0;
const JSEARCH_MIN_INTERVAL = 2000; // 2 seconds

const jsearchCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function rateLimitedFetch(url, options = {}) {
  const now = Date.now();
  const wait = Math.max(0, JSEARCH_MIN_INTERVAL - (now - lastJSearchCall));
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
  lastJSearchCall = Date.now();
  return fetch(url, options);
}

// ── Helpers ───────────────────────────────────────────────────
const safeDate = (val) => {
  if (!val) return 0;
  if (val?.toDate) return val.toDate().getTime();
  const d = new Date(val);
  return isNaN(d) ? 0 : d.getTime();
};

const sortByDate = (jobs) =>
  [...jobs].sort((a, b) => safeDate(b.datePosted) - safeDate(a.datePosted));

const dedupeById = (jobs) => {
  const seen = new Set();
  return jobs.filter(j => {
    if (seen.has(j.id)) return false;
    seen.add(j.id);
    return true;
  });
};

// ── Normalisers ───────────────────────────────────────────────
const normaliseAdzuna = (job) => ({
  id:          `adzuna-${job.id}`,
  title:       job.title || "Untitled",
  company:     job.company?.display_name || "Unknown",
  location:    job.location?.display_name || "South Africa",
  description: job.description || "",
  url:         job.redirect_url || "",
  salary:      job.salary_min
    ? `R${Math.round(job.salary_min).toLocaleString("en-ZA")} – R${Math.round(job.salary_max).toLocaleString("en-ZA")}`
    : null,
  datePosted: job.created || new Date().toISOString(),
  source:     "Adzuna",
  // contract_time: "full_time" | "part_time" → permanent; "contract" → freelance
  jobType:    job.contract_time === "contract" ? "freelance" : "permanent",
});

const normaliseJSearch = (job) => ({
  id:          `jsearch-${job.job_id}`,
  title:       job.job_title || "Untitled",
  company:     job.employer_name || "Unknown",
  location:    job.job_city
    ? `${job.job_city}, ${job.job_country || "ZA"}`
    : (job.job_country || "South Africa"),
  description: job.job_description || "",
  url:         job.job_apply_link || "",
  salary:      job.job_min_salary
    ? `${job.job_salary_currency || "R"}${Math.round(job.job_min_salary).toLocaleString("en-ZA")} – ${Math.round(job.job_max_salary).toLocaleString("en-ZA")}`
    : null,
  datePosted:  job.job_posted_at_datetime_utc || new Date().toISOString(),
  source:      job.job_publisher || "JSearch",
  jobType:     job.job_employment_type === "CONTRACTOR" ? "freelance" : "permanent",
  // preserve for SA filter below
  _country:    (job.job_country || "").toUpperCase(),
  _isRemote:   !!job.job_is_remote,
});

// SA city/province/country keywords — any job whose location matches is kept.
// "remote" is always kept regardless of origin country (SA freelancers apply globally).
const SA_LOCATION_RE = /\b(south africa|gauteng|western cape|kwazulu.natal|eastern cape|limpopo|mpumalanga|north west|free state|northern cape|johannesburg|pretoria|cape town|durban|sandton|centurion|midrand|soweto|ekurhuleni|tshwane|bloemfontein|port elizabeth|gqeberha|polokwane|nelspruit|george|east london|pietermaritzburg|stellenbosch|remote|worldwide|global)\b/i;

// Applied to ALL sources — drops Argentina, Brazil, Netherlands etc.
const isSARelevant = (job) => SA_LOCATION_RE.test(job.location || "");

// Non-English Latin-script words that slip through the Latin ratio check
const NON_ENGLISH_TITLE_RE = /\b(penulis|konten|untuk|dengan|yang|kreatif|kami|adalah|bisa|akan|juga|dari|dalam|anda|atau|serta|bahwa|kita|oleh|lebih|nous|faisons|votre|notre|créations|leur|leurs|vous\b|pelo|pela|nossa|nosso|você|também|somos|hacemos|nuestro|nosotros|estamos|mira|شراكات|إشراف|تشطيبات|بالسعودية|مطلوب|وظيفة|عمل)\b/i;

// Drops garbled UTF-8/Arabic/Chinese (low Latin ratio) AND Latin-script non-English
// (Indonesian, French, Portuguese — caught by word detection above)
const isEnglishContent = (job) => {
  const title = job.title || "";
  const latinChars = (title.match(/[a-zA-Z0-9\s.,!?()\-'"]/g) || []).length;
  if (latinChars / Math.max(title.length, 1) <= 0.55) return false;
  if (NON_ENGLISH_TITLE_RE.test(title)) return false;
  return true;
};

// ── Adzuna (via /api/adzuna proxy) ───────────────────────────
export const fetchAdzunaJobs = async ({
  keywords = "",
  location = "south africa",
  page     = 1,
  jobType  = null,
  signal   = null,
} = {}) => {
  if (!ADZUNA_APP_ID || !ADZUNA_APP_KEY) {
    console.warn("Adzuna: missing API keys in .env");
    return [];
  }
  try {
    const params = new URLSearchParams({
      app_id:           ADZUNA_APP_ID,
      app_key:          ADZUNA_APP_KEY,
      results_per_page: 20,
      what:             keywords || "jobs",
      where:            location || "south africa",
      sort_by:          "date",
    });
    if (jobType === "permanent") params.set("permanent", "1");
    if (jobType === "contract")  params.set("contract",  "1");

    // 12s timeout — prevents a slow Adzuna from blocking JSearch results
    const timeoutId = setTimeout(() => controller.abort(), 12000);
    const controller = new AbortController();
    const combinedSignal = signal
      ? (AbortSignal.any ? AbortSignal.any([signal, controller.signal]) : controller.signal)
      : controller.signal;

    let res;
    try {
      res = await fetch(`/api/adzuna/v1/api/jobs/za/search/${page}?${params}`, { signal: combinedSignal });
    } finally {
      clearTimeout(timeoutId);
    }
    if (!res.ok) { console.warn("Adzuna error:", res.status); return []; }
    const data = await res.json();
    return (data.results || []).map(normaliseAdzuna);
  } catch (err) {
    if (err.name !== "AbortError") console.warn("Adzuna fetch failed:", err.message);
    return [];
  }
};

// ── JSearch (with rate limiting + cache) ─────────────────────
export const fetchJSearchJobs = async ({
  keywords       = "",
  location       = "South Africa",
  page           = 1,
  employmentType = null,
  signal         = null,
} = {}) => {
  if (!JSEARCH_KEY) {
    console.warn("JSearch: missing REACT_APP_JSEARCH_API_KEY in .env");
    return [];
  }

  const cacheKey = `${keywords}|${location}|${page}|${employmentType}`;
  const cached = jsearchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  try {
    const q   = keywords
      ? `${keywords} in ${location || "South Africa"}`
      : `jobs in ${location || "South Africa"}`;

    let url = `https://jsearch.p.rapidapi.com/search?query=${encodeURIComponent(q)}&page=${page}&num_pages=1&country=za&date_posted=month`;
    if (employmentType) url += `&employment_types=${employmentType}`;

    const res = await rateLimitedFetch(url, {
      headers: {
        "x-rapidapi-host": "jsearch.p.rapidapi.com",
        "x-rapidapi-key":  JSEARCH_KEY,
      },
      signal,
    });

    if (!res.ok) { console.warn("JSearch error:", res.status, await res.text().catch(() => "")); return []; }
    const data = await res.json();
    const jobs = (data.data || []).map(normaliseJSearch);
    jsearchCache.set(cacheKey, { data: jobs, timestamp: Date.now() });
    return jobs;
  } catch (err) {
    if (err.name !== "AbortError") console.warn("JSearch fetch failed:", err.message);
    return [];
  }
};

// ── RemoteOK — free public API, global remote jobs ────────────
// First element is a legal notice object — always slice(1)
const normaliseRemoteOK = (job) => ({
  id:          `remoteok-${job.id}`,
  title:       job.position || `${job.company} Remote Role`,
  company:     job.company  || "Unknown",
  location:    "Remote" + (job.location && !/worldwide|anywhere/i.test(job.location) ? ` · ${job.location}` : ""),
  description: (job.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 800),
  url:         job.apply_url || job.url || "",
  salary:      job.salary || null,
  datePosted:  job.epoch  ? new Date(job.epoch * 1000).toISOString() : new Date().toISOString(),
  source:      "RemoteOK",
  jobType:     "freelance",
  tags:        Array.isArray(job.tags) ? job.tags : [],
});

export const fetchRemoteOKJobs = async ({ signal = null } = {}) => {
  try {
    const res = await fetch("https://remoteok.com/api", {
      headers: { "User-Agent": "GigScout/1.0 (gigscout.co.za)" },
      signal,
    });
    if (!res.ok) { console.warn("RemoteOK error:", res.status); return []; }
    const data = await res.json();
    // Filter for Africa/SA-relevant tags or just return all remote roles
    return data.slice(1)
      .filter(j => j && j.position)
      .map(normaliseRemoteOK);
  } catch (err) {
    if (err.name !== "AbortError") console.warn("RemoteOK fetch failed:", err.message);
    return [];
  }
};

// ── Firestore local gigs ──────────────────────────────────────
export const fetchLocalJobs = async ({ maxLimit = 100, signal = null } = {}) => {
  try {
    const jobs = await getLocalJobs({ approved: true, maxLimit, source: null });
    return jobs.map(j => ({
      ...j,
      datePosted: j.datePosted?.toDate
        ? j.datePosted.toDate().toISOString()
        : (j.datePosted || new Date().toISOString()),
    }));
  } catch (err) {
    if (err.name !== "AbortError") console.warn("fetchLocalJobs failed:", err.message);
    return [];
  }
};

// ── Client-side keyword chip filter ──────────────────────────
export const filterJobs = (jobs, keywords = []) => {
  const valid = keywords.map(k => (k || "").trim().toLowerCase()).filter(Boolean);
  if (!valid.length) return jobs;
  return jobs.filter(job => {
    const text = `${job.title} ${job.description} ${job.company}`.toLowerCase();
    return valid.some(kw => text.includes(kw));
  });
};

// Keywords whose presence in a title/description marks a job as freelance-adjacent.
// SA APIs rarely populate contract_time / employment_type metadata, so we fall back
// to content matching.
const FREELANCE_TEXT_SIGNALS = [
  'freelance', 'contract', 'temp ', 'temporary', 'part-time', 'part time',
  'casual', 'fixed term', 'short-term', 'project-based', 'gig',
];

// ── Main combined fetch (supports signal for cancellation) ────
export const fetchCombinedJobs = async ({
  keywords = "",
  location = "South Africa",
  typeMode = "all",
  page     = 1,
  signal   = null,
} = {}) => {
  const kw = (keywords || "").trim();

  // In freelance mode: if the user hasn't typed freelance/contract keywords, inject
  // them so APIs return relevant results (SA APIs barely use the type metadata).
  let searchKw = kw || "south africa";
  if (typeMode === "freelance" && !/freelance|contract/i.test(searchKw)) {
    searchKw = kw ? `freelance contract ${kw}` : "freelance contract south africa";
  }

  const adzunaJobType  =
    typeMode === "permanent" ? "permanent" :
    typeMode === "freelance" ? "contract"  : null;

  const jsearchEmpType =
    typeMode === "permanent" ? "FULLTIME"   :
    typeMode === "freelance" ? "CONTRACTOR" : null;

  const includeLocal = typeMode !== "permanent";

  // RemoteOK: only fetch on first page to avoid hammering their API
  const includeRemote = typeMode !== "permanent" && page === 1;

  const [adzunaRes, jsearchRes, localRes, remoteOKRes] = await Promise.allSettled([
    fetchAdzunaJobs({ keywords: searchKw, location, page, jobType: adzunaJobType, signal }),
    fetchJSearchJobs({ keywords: searchKw, location, page, employmentType: jsearchEmpType, signal }),
    includeLocal  ? fetchLocalJobs({ maxLimit: 100, signal }) : Promise.resolve([]),
    includeRemote ? fetchRemoteOKJobs({ signal })             : Promise.resolve([]),
  ]);

  const adzuna   = adzunaRes.status   === "fulfilled" ? adzunaRes.value   : [];
  const jsearch  = jsearchRes.status  === "fulfilled" ? jsearchRes.value  : [];
  const local    = localRes.status    === "fulfilled" ? localRes.value    : [];
  // Cap RemoteOK — they're global and flood the list with fresh-dated international jobs
  const remoteOK = (remoteOKRes.status === "fulfilled" ? remoteOKRes.value : []).slice(0, 12);

  // SA-specific sources first (sorted by date), RemoteOK appended after to avoid
  // it dominating just because its epoch timestamps are always "fresh".
  const saPool     = sortByDate(dedupeById([...local, ...adzuna, ...jsearch]))
    .filter(isSARelevant)
    .filter(isEnglishContent);
  const remotePool = remoteOK.filter(isEnglishContent);
  let merged = dedupeById([...saPool, ...remotePool]);

  if (typeMode === "freelance") {
    // Local classified ads (source="Freelance") are genuine gigs even if they don't
    // say "freelance" — but still exclude jobs explicitly marked permanent.
    merged = merged.filter(j => {
      if (j.jobType === "permanent") return false;
      if (j.source === "Freelance" || j.source === "RemoteOK") return true;
      if (j.jobType === "freelance") return true;
      const text = `${j.title} ${j.description}`.toLowerCase();
      return FREELANCE_TEXT_SIGNALS.some(sig => text.includes(sig));
    });
  } else if (typeMode === "permanent") {
    merged = merged.filter(j => j.jobType === "permanent");
  }

  // Keyword relevance — local (Firestore) and RemoteOK return ALL jobs regardless
  // of query, so we filter the full merged set here. Strip injected terms first.
  const kwFilter = kw
    .replace(/\bfreelance\s+contract\s+/i, "")
    .replace(/\bsouth\s+africa\b/gi, "")
    .trim();

  if (kwFilter.length > 0) {
    const words = kwFilter.toLowerCase().split(/[\s,]+/).filter(w => w.length > 2);
    if (words.length > 0) {
      merged = merged.filter(job => {
        const text = `${job.title || ""} ${job.description || ""} ${job.company || ""}`.toLowerCase();
        return words.some(w => text.includes(w));
      });
    }
  }

  return merged;
};